// Shared SEO primitives. Single source of truth for the canonical origin,
// default social preview image and structured-data helpers.

import { LOCALE_PREFIX } from "@/i18n/config";
import { detectLocale } from "@/i18n/detect";

export const SITE_URL = "https://bidnovaauctions.uk";
export const SITE_NAME = "BidNova Auctions";

/** Absolute URL of the default social sharing card (1200x630). */
export const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.jpg`;

/**
 * Absolute URL for the currently active locale. English keeps its existing
 * unprefixed URLs; other locales self-reference their prefixed variant so
 * canonical / og:url never point at a different language.
 */
export function absoluteUrl(path: string): string {
  if (!path) return SITE_URL;
  if (/^https?:\/\//i.test(path)) return path;
  const rel = path.startsWith("/") ? path : `/${path}`;
  let prefix = "";
  try {
    prefix = LOCALE_PREFIX[detectLocale()] ?? "";
  } catch {
    prefix = "";
  }
  return `${SITE_URL}${prefix}${rel}`;
}

/**
 * og:image + twitter:image pair. Leaf routes only — never add these on
 * __root or a layout route, or they would override every child page.
 */
export function socialImageMeta(
  image?: string | null,
  alt = "BidNova Auctions — UK's trusted trading card marketplace",
) {
  const url = image ? absoluteUrl(image) : DEFAULT_OG_IMAGE;
  return [
    { property: "og:image", content: url },
    { property: "og:image:alt", content: alt },
    { name: "twitter:image", content: url },
    { name: "twitter:card", content: "summary_large_image" },
  ];
}

export type Crumb = { name: string; path: string };

/** schema.org BreadcrumbList JSON-LD for a route's head().scripts. */
export function breadcrumbLd(crumbs: Crumb[]) {
  return {
    type: "application/ld+json",
    children: JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: crumbs.map((c, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: c.name,
        item: absoluteUrl(c.path),
      })),
    }),
  };
}
